import { Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { isValidDatetime } from '@woovi/dates';
import { BrowserOnly, Countdown } from '@woovi/ui';

import type { PaymentMethodPaymentDeadline_charge$key } from './__generated__/PaymentMethodPaymentDeadline_charge.graphql.ts';
import { CHARGE_EXPIRES_TYPE_ENUM } from '@woovi/shared';

type PaymentMethodPaymentDeadlineProps = {
  charge: PaymentMethodPaymentDeadline_charge$key;
};
const PaymentMethodPaymentDeadline = (
  props: PaymentMethodPaymentDeadlineProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge = useFragment<PaymentMethodPaymentDeadline_charge$key>(
    graphql`
      fragment PaymentMethodPaymentDeadline_charge on Charge {
        expiresDate
        createdAt
        company {
          id
          nameFriendly
          preferences {
            chargeSettings {
              expiration {
                type
                fakeValue
              }
            }
          }
        }
      }
    `,
    props.charge,
  );

  if (!isValidDatetime(charge?.expiresDate)) {
    return null;
  }

  const { preferences } = charge.company;

  //* This logic is for the fake expiration timer, don't change the order of the variables, moment is mutable and will break the value logic
  const now = moment().tz('America/Sao_Paulo');
  const chargeCreatedAt = moment(charge.createdAt).tz('America/Sao_Paulo');

  const expirationFake = preferences?.chargeSettings?.expiration?.fakeValue;

  const expirationFakeMs = chargeCreatedAt.add(expirationFake, 'seconds');

  const getExpirationFromType = (): string => {
    if (
      charge?.company?.preferences?.chargeSettings?.expiration?.type ===
      CHARGE_EXPIRES_TYPE_ENUM.FAKE
    ) {
      const isFakeExpired = now.isAfter(expirationFakeMs);

      if (isFakeExpired) {
        return charge?.expiresDate;
      }

      return expirationFakeMs.toISOString();
    }

    return charge?.expiresDate;
  };
  //*

  if (
    charge?.company?.preferences?.chargeSettings?.expiration?.type !==
    CHARGE_EXPIRES_TYPE_ENUM.NONE
  ) {
    return (
      <Stack sx={{ alignItems: 'center' }}>
        <Typography variant='h6' fontWeight='bold' textAlign='center'>
          {t('Payment Deadline')}
        </Typography>
        <BrowserOnly>
          <Countdown
            targetDate={getExpirationFromType()}
            sx={{ color: theme.palette.text.secondary }}
          />
        </BrowserOnly>
      </Stack>
    );
  }

  return null;
};

export default PaymentMethodPaymentDeadline;
